"use client";

import { ReactNode } from "react";
import { FiX } from "react-icons/fi";

import { Button, type ButtonProps } from "@/components/ui";

import { cx } from "@/lib/tailwind-utils";

export interface BulkAction {
  /** Unique identifier for the action */
  id: string;
  /** Label to display on the button */
  label: string;
  /** Icon to display before the label */
  icon?: ReactNode;
  /** Button variant (default: "secondary") */
  variant?: ButtonProps["variant"];
  /** Click handler */
  onClick: () => void;
  /** Whether the action is disabled */
  disabled?: boolean;
  /** Whether to show count in the label (e.g., "Rename (5)") */
  showCount?: boolean;
}

export interface BulkActionsBarProps {
  /** Number of items currently selected */
  selectedCount: number;
  /** Singular label for what's being selected (e.g., "service", "photo") */
  itemLabel?: string;
  /**
   * Plural form, for nouns "+s" gets wrong ("opportunities", "people").
   * Defaults to `${itemLabel}s`.
   */
  itemLabelPlural?: string;
  /** Optional control displayed before the selected count, such as a select-all checkbox */
  leadingContent?: ReactNode;
  /** Actions to display */
  actions: BulkAction[];
  /** Handler when clear/cancel is clicked */
  onClear: () => void;
  /** Whether any action is currently loading */
  isLoading?: boolean;
  /** Additional class name for the container */
  className?: string;
  /** Position style: "sticky" anchors to bottom of scroll container, "fixed" anchors to viewport */
  position?: "sticky" | "fixed";
  /** Accessible label when the bar should be exposed as a toolbar */
  ariaLabel?: string;
}

/**
 * A reusable floating action bar that appears when items are selected.
 *
 * @example
 * ```tsx
 * <BulkActionsBar
 *   selectedCount={selectedIds.size}
 *   itemLabel="service"
 *   actions={[
 *     { id: "edit", label: "Edit", icon: <FiEdit2 />, onClick: handleEdit },
 *     { id: "archive", label: "Archive", variant: "destructive", onClick: handleArchive },
 *   ]}
 *   onClear={() => setSelectedIds(new Set())}
 * />
 * ```
 */
/**
 * Picks the plural category for a count. `Intl.PluralRules` knows which form a
 * locale needs — it has no way to inflect a noun into that form, so the caller
 * supplies the words and this only chooses between them.
 */
const pluralRules = new Intl.PluralRules("en-US");

export function BulkActionsBar({
  selectedCount,
  itemLabel = "item",
  itemLabelPlural,
  leadingContent,
  actions,
  onClear,
  isLoading = false,
  className,
  position = "sticky",
  ariaLabel,
}: BulkActionsBarProps) {
  if (selectedCount === 0) return null;

  const pluralLabel =
    pluralRules.select(selectedCount) === "one"
      ? itemLabel
      : (itemLabelPlural ?? `${itemLabel}s`);

  return (
    <div
      data-bulk-actions-bar=""
      role={ariaLabel ? "toolbar" : undefined}
      aria-label={ariaLabel}
      className={cx(
        position === "fixed"
          ? "fixed bottom-6 left-1/2 z-50 -translate-x-1/2"
          : "sticky bottom-6 z-50 mx-auto",
        // Query the available container width, not the bar's intrinsic content
        // width. Keep the visible surface centred and sized to its contents.
        // The full-width strip is invisible, so clicks pass through it to the
        // page underneath; only the bar itself takes them.
        "@container/bulk-actions pointer-events-none w-full",
        position === "fixed"
          ? "max-w-[calc(100vw-1rem)]"
          : "max-w-[min(100%,calc(100vw-1rem))]",
        "animate-in slide-in-from-bottom-4 duration-200",
        className,
      )}
    >
      <div
        className={cx(
          "pointer-events-auto mx-auto flex w-fit max-w-full flex-wrap items-center justify-center gap-x-3 gap-y-2 rounded-lg bg-background/80 px-3 py-2.5 backdrop-blur-sm",
          "shadow-md",
        )}
      >
        {/* Close button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          disabled={isLoading}
          aria-label="Clear selection"
          icon={<FiX />}
        />

        {leadingContent && (
          <div className="flex shrink-0 items-center">{leadingContent}</div>
        )}

        {/* Selection count */}
        <div className="hidden items-center gap-2 @[32rem]/bulk-actions:flex">
          <span className="flex h-6 min-w-6 items-center justify-center rounded-md bg-muted px-1.5 text-body-strong tabular-nums text-foreground-secondary">
            {selectedCount}
          </span>
          <span className="text-body text-foreground-secondary">
            {pluralLabel} selected
          </span>
        </div>

        {/* Divider */}
        <div className="hidden h-5 w-px bg-muted @[32rem]/bulk-actions:block" />

        {/* Actions */}
        <div className="flex flex-wrap items-center justify-center gap-1.5">
          {actions.map((action) => (
            <Button
              key={action.id}
              onClick={action.onClick}
              disabled={action.disabled || isLoading}
              variant={action.variant || "ghost"}
              className={cx("!py-1")}
              icon={action.icon}
            >
              {action.showCount
                ? `${action.label} (${selectedCount})`
                : action.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default BulkActionsBar;
